import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Home, Star, Clock, Calendar, Globe, Film, Play, X } from 'lucide-react';
import { getMovieDetails } from '../api/tmdb';
import { getPosterUrl, getBackdropUrl, formatRuntime, calculateOurRating, getProfileUrl } from '../utils/helpers';
import { GENRES } from '../utils/constants';
import type { Movie, CastMember, CrewMember } from '../types/movie';

interface VideoResult {
  id: string;
  key: string;
  name: string;
  site: string;
  type: string;
}

interface MovieDetails extends Movie {
  budget?: number;
  revenue?: number;
  tagline?: string;
  production_companies?: { id: number; name: string; logo_path: string | null }[];
  spoken_languages?: { english_name: string; iso_639_1: string }[];
  credits?: {
    cast: CastMember[];
    crew: CrewMember[];
  };
  videos?: {
    results: VideoResult[];
  };
}

export function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [movie, setMovie] = useState<MovieDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTrailer, setShowTrailer] = useState(false);

  useEffect(() => {
    async function fetchMovie() {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const data = await getMovieDetails(parseInt(id));
        setMovie(data);
      } catch (err) {
        console.error('Error fetching movie:', err);
        setError('Failed to load movie details');
      } finally {
        setLoading(false);
      }
    }

    fetchMovie();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading movie details...</p>
        </div>
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Movie not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const directors = movie.credits?.crew.filter((c) => c.job === 'Director').slice(0, 2) || [];
  const writers = movie.credits?.crew.filter((c) => c.department === 'Writing').slice(0, 4) || [];
  const cast = movie.credits?.cast.slice(0, 12) || [];
  const ourRating = calculateOurRating(movie.imdb_rating, movie.rt_rating);
  const year = movie.release_date?.split('-')[0];

  // Find trailer from videos
  const trailerKey = movie.videos?.results.find(
    (v) => v.site === 'YouTube' && (v.type === 'Trailer' || v.type === 'Teaser')
  )?.key;

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Navigation buttons - fixed at top */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/60 to-transparent">
        <div className="max-w-screen-xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-3 py-1.5 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full text-white/90 hover:text-white transition-all"
            >
              <ArrowLeft size={18} />
              <span className="text-sm">Back</span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-3 py-1.5 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full text-white/90 hover:text-cyan-400 transition-all"
            >
              <Home size={18} />
              <span className="text-sm">Home</span>
            </button>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {movie.backdrop_path && (
        <div className="relative h-[50vh] overflow-hidden">
          <img
            src={getBackdropUrl(movie.backdrop_path, 'large')}
            alt={movie.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />

          {/* Play Trailer Button */}
          {trailerKey && (
            <button
              onClick={() => setShowTrailer(true)}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 px-5 py-2.5 bg-black/50 hover:bg-black/70 backdrop-blur-sm border border-white/20 rounded-full text-white/90 hover:text-white transition-all group"
            >
              <Play size={18} className="fill-current" />
              <span className="text-sm font-medium">Play Trailer</span>
            </button>
          )}
        </div>
      )}

      {/* Trailer Modal */}
      {showTrailer && trailerKey && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={() => setShowTrailer(false)}
        >
          <div className="relative w-full max-w-5xl mx-4 aspect-video">
            <button
              onClick={() => setShowTrailer(false)}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
            >
              <X size={28} />
            </button>
            <iframe
              src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1`}
              className="w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Movie info */}
      <div className="max-w-screen-xl mx-auto px-4 pb-12">
        <div className="flex flex-col md:flex-row gap-8 -mt-32 relative z-10">
          {/* Poster */}
          <div className="flex-shrink-0">
            <div className="w-64 aspect-[2/3] rounded-xl overflow-hidden shadow-2xl">
              {movie.poster_path ? (
                <img
                  src={getPosterUrl(movie.poster_path, 'large')}
                  alt={movie.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-slate-800 flex items-center justify-center">
                  <Film size={64} className="text-slate-600" />
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="flex-1 pt-4">
            {/* Title */}
            <h1 className="text-4xl font-bold text-white mb-2">{movie.title}</h1>

            {/* Tagline */}
            {movie.tagline && (
              <p className="text-lg text-slate-400 italic mb-4">"{movie.tagline}"</p>
            )}

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-4 text-slate-300 mb-6">
              {year && (
                <div className="flex items-center gap-1.5">
                  <Calendar size={16} className="text-slate-500" />
                  <span>{year}</span>
                </div>
              )}
              {movie.runtime && (
                <div className="flex items-center gap-1.5">
                  <Clock size={16} className="text-slate-500" />
                  <span>{formatRuntime(movie.runtime)}</span>
                </div>
              )}
              {movie.original_language && (
                <div className="flex items-center gap-1.5">
                  <Globe size={16} className="text-slate-500" />
                  <span className="uppercase">{movie.original_language}</span>
                </div>
              )}
            </div>

            {/* Genres */}
            <div className="flex flex-wrap gap-2 mb-6">
              {movie.genre_ids?.map((genreId) => (
                <span
                  key={genreId}
                  className="px-3 py-1 bg-slate-800 text-slate-300 rounded-full text-sm"
                >
                  {GENRES[genreId] || `Genre ${genreId}`}
                </span>
              ))}
            </div>

            {/* Ratings */}
            <div className="flex flex-wrap gap-4 mb-6">
              {ourRating && (
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-violet-500/20 border border-purple-500/50 rounded-xl">
                  <Star size={20} className="text-purple-400" />
                  <div>
                    <div className="text-xl font-bold text-white">{ourRating.toFixed(1)}</div>
                    <div className="text-xs text-purple-300">Our Rating</div>
                  </div>
                </div>
              )}
              {movie.imdb_rating ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/50 rounded-xl">
                  <span className="text-sm font-bold text-yellow-400">IMDb</span>
                  <div>
                    <div className="text-xl font-bold text-white">{movie.imdb_rating.toFixed(1)}</div>
                    <div className="text-xs text-yellow-300">Rating</div>
                  </div>
                </div>
              ) : movie.vote_average > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/50 rounded-xl">
                  <span className="text-sm font-bold text-yellow-400">TMDB</span>
                  <div>
                    <div className="text-xl font-bold text-white">{movie.vote_average.toFixed(1)}</div>
                    <div className="text-xs text-yellow-300">Rating</div>
                  </div>
                </div>
              )}
              {movie.rt_rating && (
                <div className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/50 rounded-xl">
                  <span className="text-lg">🍅</span>
                  <div>
                    <div className="text-xl font-bold text-white">{movie.rt_rating}%</div>
                    <div className="text-xs text-red-300">Rotten Tomatoes</div>
                  </div>
                </div>
              )}
            </div>

            {/* Overview */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-white mb-2">Overview</h2>
              <p className="text-slate-300 leading-relaxed">{movie.overview}</p>
            </div>

            {/* Director & Writers with Photos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {directors.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">
                    {directors.length > 1 ? 'Directors' : 'Director'}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {directors.map((director) => (
                      <Link
                        key={director.id}
                        to={`/person/${director.id}`}
                        className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors group"
                      >
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-700 flex-shrink-0">
                          {director.profile_path ? (
                            <img
                              src={getProfileUrl(director.profile_path, 'small')}
                              alt={director.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-500">
                              <span className="text-xl">👤</span>
                            </div>
                          )}
                        </div>
                        <span className="text-white group-hover:text-cyan-400 transition-colors font-medium">
                          {director.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {writers.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Writers</h3>
                  <div className="flex flex-wrap gap-3">
                    {writers.map((writer, index) => (
                      <Link
                        key={`${writer.id}-${index}`}
                        to={`/person/${writer.id}`}
                        className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors group"
                      >
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-700 flex-shrink-0">
                          {writer.profile_path ? (
                            <img
                              src={getProfileUrl(writer.profile_path, 'small')}
                              alt={writer.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-500">
                              <span className="text-xl">👤</span>
                            </div>
                          )}
                        </div>
                        <span className="text-white group-hover:text-cyan-400 transition-colors font-medium">
                          {writer.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cast */}
        {cast.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-white mb-6">Cast</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {cast.map((actor) => (
                <Link
                  key={actor.id}
                  to={`/person/${actor.id}`}
                  className="group"
                >
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-slate-800 mb-2">
                    {actor.profile_path ? (
                      <img
                        src={`https://image.tmdb.org/t/p/w185${actor.profile_path}`}
                        alt={actor.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-600">
                        <span className="text-4xl">👤</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-white group-hover:text-cyan-400 transition-colors">
                    {actor.name}
                  </h3>
                  <p className="text-xs text-slate-500 truncate">{actor.character}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
